import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from 'react-bootstrap';
import { toast } from 'react-toastify';

import { useUserContext } from 'providers/UserProvider';

import Table from 'components/common/Table';
import GrnCreateModal from 'components/purchase-order/purchase-order/GrnCreateModal';

import { purchaseOrderApi, grnApi } from 'api';

import {
  getPurchaseOrderListColumns,
  PURCHASE_ORDER_FILTER_OPTIONS,
} from 'constants/purchaseOrder';

import paths from 'routes/paths';

const PurchaseOrders = () => {
  const navigate = useNavigate();

  const { selectedTenant, selectedWarehouse } = useUserContext();

  const tenant = selectedTenant?.domains?.[0]?.domain;
  const warehouse = selectedWarehouse?.domain;
  const initialFilter = { warehouse: selectedWarehouse?.id };

  const [selectedPO, setSelectedPO] = useState(null);
  const [showGrnCreateModal, setShowGrnCreateModal] = useState(false);

  const headerActions = (
    <Button
      variant="primary"
      size="sm"
      onClick={() => navigate(paths.purchaseOrder.create(tenant, warehouse))}
    >
      Create PO
    </Button>
  );

  const openGrnCreateModal = purchaseOrder => {
    setSelectedPO(purchaseOrder);
    setShowGrnCreateModal(true);
  };

  const closeGrnCreateModal = () => {
    setShowGrnCreateModal(false);
    setSelectedPO(null);
  };

  const handleCreateGRN = async () => {
    if (!selectedPO) return;

    try {
      const response = await grnApi.create({
        purchase_order: selectedPO.id,
      });

      toast.success('GRN created successfully');
      navigate(paths.grns.details(tenant, warehouse, response.id));
    } catch (error) {
      toast.error('Failed to create GRN');
      closeGrnCreateModal();
    }
  };

  const extendedColumns = useMemo(() => {
    const createColumn = {
      id: 'create',
      header: 'Action',
      enableSorting: false,
      meta: {
        headerProps: { className: 'text-center' },
        cellProps: { className: 'text-center' },
      },
      cell: ({ row: { original } }) => {
        const { id, po_status } = original;

        const showCreate = ['in transit', 'approved'].includes(po_status);

        if (!showCreate) return null;

        return (
          <Button
            variant="primary"
            size="sm"
            className="text-white"
            onClick={() => openGrnCreateModal(original)}
          >
            Create GRN
          </Button>
        );
      },
    };

    return [...getPurchaseOrderListColumns(tenant, warehouse), createColumn];
  }, [tenant, warehouse]);

  return (
    <>
      <Table
        title="PO List"
        apiService={purchaseOrderApi}
        sortable={true}
        columns={extendedColumns}
        filterOptions={PURCHASE_ORDER_FILTER_OPTIONS}
        headerActions={headerActions}
        initialFilter={initialFilter}
      />

      <GrnCreateModal
        show={showGrnCreateModal}
        onHide={closeGrnCreateModal}
        onConfirm={handleCreateGRN}
        selectedItem={selectedPO}
        type="po"
      />
    </>
  );
};

export default PurchaseOrders;
